import * as React from 'react';
import {
  Grid,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Paper,
} from '@material-ui/core';
import type { AxiosError } from 'axios';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { Autocomplete, Skeleton } from '@material-ui/lab';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { emptyJobDetail } from '../../policyConfigurator/shared/Utilities/PolicyConfiguratorHelpers';
import type { ILabelValuePair } from '../../marking/MarkingInterfaces';
import getJobTypes from '../../../lib/SWRfetchers/getJobTypes';
import type { IJobDetail } from './JobInterfaces';
import getStationTypes from '../../../lib/SWRfetchers/getStationTypes';
import AddFieldOptions from './AddFieldOptions';

const JobUpsert: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const jobIdParam = searchParams.get('jobId');

  const [jobFormValues, setJobFormValues] = React.useState<IJobDetail>(emptyJobDetail);
  const [isFetching, setIsFetching] = React.useState<boolean>(false);
  const [editMode, setEditMode] = React.useState<boolean>(false);

  const { jobTypes } = getJobTypes();
  const { stationTypes } = getStationTypes();

  const getJobInfo = () => axios.get<IJobDetail>('/job/GetJob', {
    params: {
      jobId: jobIdParam,
    },
  })
    .then((result) => {
      if (result.data) {
        setJobFormValues(result.data);
      }
      return result.data;
    })
    .catch((error: AxiosError) => {
      if (error?.message) {
        enqueueSnackbar('Unable to find job.', { persist: false, variant: 'error' });
      }
    });

  const handleSaveBtnClicked = () => {
    if ((jobFormValues.jobFields.some((field) => (field.fieldName === '') || (field.fieldOptions.length === 0))
        || (jobFormValues.jobFields.some((field) => field.fieldOptions.some((fieldOption) => fieldOption.name === '')))
        || jobFormValues.jobCode === ''
    )) {
      enqueueSnackbar('Cannot save the job information, while field and field options are not filled out.', { variant: 'error' });
    } else {
      setIsFetching(true);
      axios.post<IJobDetail>('/job/upsert', {
        jobId: jobFormValues.jobId ? jobFormValues.jobId : '00000000-0000-0000-0000-000000000000',
        jobCode: jobFormValues.jobCode,
        jobType: jobFormValues.jobType.label,
        stationType: jobFormValues?.stationType?.label,
        jobFields: jobFormValues.jobFields,
      })
        .then(() => {
          if (editMode) {
            enqueueSnackbar(`Updated Job: ${jobFormValues.jobCode}.`, { variant: 'success' });
            getJobInfo().catch(() => {});
            navigate(`/job/?jobId=${jobFormValues.jobId}`);
          } else {
            enqueueSnackbar(`Added Job: ${jobFormValues.jobCode}.`, { variant: 'success' });
            navigate('/jobs');
          }
        })
        .catch((error: AxiosError) => {
          if (error?.message) {
            enqueueSnackbar(`Unable to save job information: ${error?.message}`, { persist: false, variant: 'error' });
          }
        })
        .finally(() => {
          setIsFetching(false);
        });
    }
  };

  React.useEffect(() => {
    if (jobIdParam) {
      setEditMode(true);
      getJobInfo().catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enqueueSnackbar, jobIdParam]);

  return (
    <>
      <Paper style={{ marginBottom: '2rem' }}>
        <Grid container spacing={0}>
          <Grid lg={8} md={5} xs={12}>
            <Grid item>
              <Box p={2}>
                <Typography variant="h6">
                  {'Job Information'}
                </Typography>
              </Box>
            </Grid>
            <Grid item>
              <Box p={2}>
                <TextField
                  value={jobFormValues.jobCode}
                  id="jobCode-value-input"
                  label="Job Code"
                  variant="outlined"
                  type="string"
                  fullWidth
                  onChange={(event) => {
                    const inputVal = event.target.value;
                    const tempjobFormValues: IJobDetail = { ...jobFormValues, jobCode: inputVal };
                    setJobFormValues(tempjobFormValues);
                  }}
                />
              </Box>
            </Grid>
          </Grid>
          <Grid lg={8} md={5} item xs={5}>
            <Grid item>
              <Box p={2}>
                {
                jobTypes
                  ? (
                    <Autocomplete
                      value={jobFormValues.jobType || null}
                      onChange={(e, data: ILabelValuePair | null) => {
                        if (data) {
                          const tempjobFormValues: IJobDetail = { ...jobFormValues, jobType: data };
                          setJobFormValues(tempjobFormValues);
                        } else {
                          const valuePair: ILabelValuePair = { value: '', label: '' };
                          const tempjobFormValues: IJobDetail = { ...jobFormValues, jobType: valuePair };
                          setJobFormValues(tempjobFormValues);
                        }
                      }}
                      autoHighlight
                      autoSelect
                      options={jobTypes.sort((a, b) => a.label.localeCompare(b.label))}
                      getOptionLabel={(option) => option.label.toString()}
                      // prop spreading is directly from MUI tsx example
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      renderInput={(params) => <TextField required {...params} id="jobType-input" label="Job Type" variant="outlined" />}
                    />
                  ) : (<Skeleton height="56px" variant="rect" />)
              }
              </Box>
            </Grid>
          </Grid>
          <Grid item>
            <Box p={5} />
          </Grid>
          <Grid lg={8} md={5} item xs={5}>
            <Grid item>
              <Box p={2}>
                {
                stationTypes
                  ? (
                    <Autocomplete
                      value={jobFormValues.stationType || { label: '', value: '' }}
                      onChange={(event: unknown, selectedStationType: ILabelValuePair | null) => {
                        setJobFormValues({ ...jobFormValues, stationType: selectedStationType || { label: '', value: '' } });
                      }}
                      options={stationTypes}
                      autoHighlight
                      autoSelect
                      getOptionLabel={(option) => option.label.toString()}
                      renderInput={(params) => (
                        <TextField
                          // prop spreading is directly from MUI tsx example
                          // eslint-disable-next-line react/jsx-props-no-spreading
                          {...params}
                          id="stationType-input"
                          label="Station Type"
                          variant="outlined"
                        />
                      )}
                    />
                  ) : <Skeleton height="56px" variant="rect" />
              }
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Box p={5} />
        </Grid>
      </Paper>
      <AddFieldOptions JobFormValues={jobFormValues} setJobFormValues={setJobFormValues} />
      <Grid container spacing={2} direction="row-reverse">
        <Grid item xs={3}>
          <Box p={6}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              disabled={isFetching}
              onClick={() => { handleSaveBtnClicked(); }}
            >
              {
                isFetching ? (<CircularProgress size={25} />) : `${!jobIdParam ? 'Add' : 'Update'} Job`
              }
            </Button>
          </Box>
        </Grid>
        <Grid item xs={3}>
          <Box p={6}>
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              onClick={() => navigate('/jobs')}
            >
              {'Back to All Jobs'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default JobUpsert;
